import type { getSupabase } from "@dwelldrop/supabase";
import { mfaToE164 } from "./utils";
export type MfaSupabaseClient = ReturnType<typeof getSupabase>;
export type MfaAssuranceSnapshot = {
  currentLevel: "aal1" | "aal2" | null;
  nextLevel: "aal1" | "aal2" | null;
};
export async function mfaGetAssuranceLevel(
  supabase: MfaSupabaseClient
): Promise<{ data: MfaAssuranceSnapshot | null; error: string | null }> {
  const { data, error } =
    await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) {
    return { data: null, error: error.message };
  }
  return {
    data: {
      currentLevel: data.currentLevel,
      nextLevel: data.nextLevel,
    },
    error: null,
  };
}
/** True when the session must complete MFA before privileged access (Supabase TOTP login flow). */
export function mfaSessionNeedsChallenge(
  snapshot: MfaAssuranceSnapshot | null
): boolean {
  if (!snapshot) return false;
  return (
    snapshot.nextLevel === "aal2" && snapshot.currentLevel !== snapshot.nextLevel
  );
}

export type MfaFactorRow = {
  id: string;
  status: "verified" | "unverified";
  friendly_name?: string;
  factor_type: string;
};

/** Includes unverified TOTP factors (listFactors() only returns verified). */
export async function mfaListAllTotpFactors(
  supabase: MfaSupabaseClient
): Promise<MfaFactorRow[]> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user?.factors) return [];
  return user.factors
    .filter((f) => f.factor_type === "totp")
    .map((f) => ({
      id: f.id,
      status: f.status as "verified" | "unverified",
      friendly_name: f.friendly_name,
      factor_type: f.factor_type,
    }));
}

/** Delete unverified TOTP factors before starting a new enrollment. */
export async function mfaCleanupUnverifiedTotpFactors(
  supabase: MfaSupabaseClient
): Promise<void> {
  const factors = await mfaListAllTotpFactors(supabase);
  for (const factor of factors) {
    if (factor.status === "unverified") {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
  }
}

/** Check if the user has a verified TOTP factor. */
export async function mfaHasVerifiedTotpFactor(
  supabase: MfaSupabaseClient
): Promise<boolean> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return false;
  return Boolean(data.totp && data.totp.length > 0);
}

/** Get the primary verified TOTP factor. */
export async function mfaGetVerifiedTotpFactor(
  supabase: MfaSupabaseClient
): Promise<{ id: string } | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data?.totp?.[0]) return null;
  return { id: data.totp[0].id };
}

// ─── Deprecated Phone Factor Helpers (Kept for Phase Transition) ─────────────

export type MfaPhoneFactorRow = {
  id: string;
  status: "verified" | "unverified";
  friendly_name?: string;
  phone?: string;
};

/** @deprecated */
export function mfaPhoneFromFactor(factor: MfaPhoneFactorRow): string | null {
  const friendly = factor.friendly_name ?? null;
  if (friendly && friendly.startsWith("+")) {
    return mfaToE164(friendly);
  }
  const raw = factor.phone ?? null;
  return raw ? mfaToE164(raw) : null;
}

/** @deprecated */
export async function mfaListAllPhoneFactors(
  supabase: MfaSupabaseClient
): Promise<MfaPhoneFactorRow[]> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user?.factors) return [];
  return user.factors
    .filter((f) => f.factor_type === "phone")
    .map((f) => ({
      id: f.id,
      status: f.status,
      friendly_name: f.friendly_name,
      phone: (f as { phone?: string }).phone,
    }));
}

/** @deprecated */
export async function mfaCleanupUnverifiedPhoneFactors(
  supabase: MfaSupabaseClient
): Promise<void> {
  const factors = await mfaListAllPhoneFactors(supabase);
  for (const factor of factors) {
    if (factor.status === "unverified") {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
  }
}

/** @deprecated Use mfaHasVerifiedTotpFactor instead */
export async function mfaHasVerifiedPhoneFactor(
  supabase: MfaSupabaseClient
): Promise<boolean> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return false;
  return Boolean(
    (data.totp && data.totp.length > 0) || (data.phone && data.phone.length > 0)
  );
}

/** @deprecated Use mfaGetVerifiedTotpFactor instead */
export async function mfaGetVerifiedPhoneFactor(
  supabase: MfaSupabaseClient
): Promise<{ id: string; phone: string | null } | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error || !data) return null;
  if (data.totp?.[0]) {
    return { id: data.totp[0].id, phone: null };
  }
  if (data.phone?.[0]) {
    const factor = data.phone[0];
    return {
      id: factor.id,
      phone: mfaPhoneFromFactor({
        id: factor.id,
        status: "verified",
        friendly_name: factor.friendly_name,
        phone: (factor as { phone?: string }).phone,
      }),
    };
  }
  return null;
}

